import * as React from "react";
import { makeStyles, Text, Button } from "@fluentui/react-components";
import {
  CircleRegular,
  CheckmarkCircle16Filled,
  Play16Regular,
} from "@fluentui/react-icons";
import CardAccent from "../shared/CardAccent";
import StatusBadge from "../shared/StatusBadge";
import ExecutionStatusCard from "./ExecutionStatusCard";
import { ACTION_STATUS } from "../../../shared/constants";

const useStyles = makeStyles({
  card: {
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    border: "1px solid var(--border-primary, #E0E2E6)",
    borderRadius: "12px",
    boxShadow: "var(--shadow-sm, 0 1px 2px rgba(11,17,26,0.06))",
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    ":hover": {
      boxShadow: "var(--shadow-md, 0 4px 8px rgba(11,17,26,0.08))",
      transform: "translateY(-1px)",
    },
  },
  content: {
    padding: "16px",
  },
  title: {
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text-primary, #0B111A)",
    marginBottom: "12px",
    display: "block",
  },
  itemsList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  itemRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "8px 10px",
    borderRadius: "8px",
    backgroundColor: "var(--bg-secondary, #F8F9FA)",
    transition: "background-color 0.15s ease",
    ":hover": {
      backgroundColor: "var(--bg-hover, #F0EDE8)",
    },
  },
  checkIcon: {
    flexShrink: 0,
    marginTop: "2px",
    color: "var(--text-tertiary, #6E747D)",
    fontSize: "16px",
  },
  checkIconCompleted: {
    flexShrink: 0,
    marginTop: "2px",
    color: "var(--color-success, #7ED956)",
    fontSize: "16px",
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemText: {
    fontSize: "13px",
    lineHeight: "1.45",
    color: "var(--text-primary, #0B111A)",
    display: "block",
    wordBreak: "break-word",
  },
  itemMeta: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "4px",
    flexWrap: "wrap",
  },
  due: {
    fontSize: "11px",
    color: "var(--text-tertiary, #6E747D)",
  },
  assignee: {
    fontSize: "11px",
    color: "var(--text-tertiary, #6E747D)",
    fontStyle: "italic",
  },
  executeBtn: {
    minWidth: "auto",
    flexShrink: 0,
    alignSelf: "center",
    fontSize: "12px",
    backgroundColor: "#FFB500",
    color: "#0B111A",
    fontWeight: "600",
    border: "none",
    ":hover": {
      backgroundColor: "#E6A300",
    },
    ":active": {
      backgroundColor: "#CC9200",
    },
  },
  executeBtnDisabled: {
    minWidth: "auto",
    flexShrink: 0,
    alignSelf: "center",
    fontSize: "12px",
    opacity: 0.6,
    cursor: "not-allowed",
  },
  executionResult: {
    marginTop: "8px",
    marginLeft: "26px",
  },
});

/**
 * Generate a deterministic actionId for an item.
 */
function getActionId(item, index) {
  return item.id || `action-${index}`;
}

/**
 * ActionItemsCard — list of action items with per-item Execute buttons and status.
 *
 * @param {{
 *   card: { title?: string, items: Array<{ text: string, due?: string, assignee?: string, id?: string }> },
 *   onExecuteAction: Function,
 *   actionStates: Object,
 *   emailContext: Object,
 * }} props
 */
const ActionItemsCard = ({ card, onExecuteAction, actionStates = {}, emailContext }) => {
  const styles = useStyles();
  const items = card.items || [];

  const handleExecute = (actionId, actionText) => {
    if (onExecuteAction) {
      onExecuteAction({ actionId, actionText });
    }
  };

  return (
    <div className={styles.card}>
      <CardAccent />
      <div className={styles.content}>
        <Text className={styles.title}>{card.title || "Action Items"}</Text>
        <div className={styles.itemsList}>
          {items.length === 0 && (
            <div className={styles.itemRow}>
              <CircleRegular className={styles.checkIcon} />
              <div className={styles.itemContent}>
                <Text className={styles.itemText}>No action items found in this email.</Text>
              </div>
            </div>
          )}
          {items.map((item, index) => {
            const actionId = getActionId(item, index);
            const state = actionStates[actionId] || {
              status: ACTION_STATUS.PENDING,
              result: null,
              error: null,
            };
            const isCompleted = state.status === ACTION_STATUS.COMPLETED;
            const isProcessing = state.status === ACTION_STATUS.PROCESSING;
            const isExecuted =
              state.status === ACTION_STATUS.COMPLETED ||
              state.status === ACTION_STATUS.FAILED;

            return (
              <div key={actionId}>
                <div className={styles.itemRow}>
                  {/* Checkbox icon */}
                  {isCompleted ? (
                    <CheckmarkCircle16Filled className={styles.checkIconCompleted} />
                  ) : (
                    <CircleRegular className={styles.checkIcon} />
                  )}

                  {/* Item content */}
                  <div className={styles.itemContent}>
                    <Text className={styles.itemText}>{item.text}</Text>
                    <div className={styles.itemMeta}>
                      {item.due && (
                        <Text className={styles.due}>Due: {item.due}</Text>
                      )}
                      {item.assignee && (
                        <Text className={styles.assignee}>@{item.assignee}</Text>
                      )}
                      {state.status !== ACTION_STATUS.PENDING && (
                        <StatusBadge status={state.status} />
                      )}
                    </div>
                  </div>

                  {/* Execute button */}
                  {!isExecuted && (
                    <Button
                      className={
                        isProcessing
                          ? styles.executeBtnDisabled
                          : styles.executeBtn
                      }
                      size="small"
                      icon={<Play16Regular />}
                      onClick={() => handleExecute(actionId, item.text)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Running..." : "Execute"}
                    </Button>
                  )}
                </div>

                {/* Inline execution result */}
                {isExecuted && (
                  <div className={styles.executionResult}>
                    <ExecutionStatusCard
                      actionId={actionId}
                      status={state.status}
                      result={state.result}
                      error={state.error}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ActionItemsCard;
